import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccessTime,
  ChevronRight,
  Download,
  OpenInNew,
  Person,
  Search,
  Storefront,
  Visibility,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useAuth, useAppMeta, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import BaseSelect from "components/Shared/Base/BaseSelect";
import {
  useLazyGetMergedBranchProvinceReportQuery,
  useLazyGetMergedBranchReportQuery,
  useLazyGetMergedBranchResponsibleReportQuery,
  useLazyGetMergedBranchShipmentReportQuery,
} from "store/services/reportApi";
import { exportExcelFile } from "utils/exportExcelFile";

const money = (value) => `Rs. ${(Number(value) || 0).toFixed(2)}`;

const userUnpaid = (row) =>
  (Number(row?.codNotPaid) || 0) + (Number(row?.cocUserNotPaid) || 0);

// Distinct "Total User Unpaid" column — COD + COC User totals, bold, with
// FIXED-width vertical separator lines on both sides. Header and data cells
// share one frame style so the two lines sit at identical, constant
// positions regardless of value length or row.
const USER_UNPAID_COLUMN_WIDTH = 170;

const userUnpaidFrameSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: USER_UNPAID_COLUMN_WIDTH,
  flexShrink: 0,
  borderLeft: 1,
  borderRight: 1,
  borderColor: "divider",
};

const UserUnpaidHeader = () => (
  <Box sx={{ ...userUnpaidFrameSx, py: 0.75, textAlign: "center" }}>
    Total User Unpaid
  </Box>
);

const UserUnpaidCell = ({ row }) => (
  <Box sx={{ ...userUnpaidFrameSx, alignSelf: "stretch", fontWeight: 700 }}>
    {money(userUnpaid(row))}
  </Box>
);

const MergedBranchReport = () => {
  const { viewEntity } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [sortBy, setSortBy] = useState("name:1");
  const [errorMessage, setErrorMessage] = useState("");
  // When true, only shipment prices whose createdAt is older than 48 hours
  // (i.e. still unpaid after 48 h) are returned by the backend. Overdue is
  // the DEFAULT view; "All Unpaid" opts out. State lives in memory only —
  // switching drill-down levels keeps it; a page refresh resets to overdue.
  const [overdueOnly, setOverdueOnly] = useState(true);

  const level = searchParams.get("level") || "provinces";
  const category = searchParams.get("category") || "cod-users";
  const provinceId = searchParams.get("provinceId") || "";
  const provinceName = searchParams.get("provinceName") || "";
  const branchId = searchParams.get("branchId") || "";
  const branchName = searchParams.get("branchName") || "";
  const responsibleId = searchParams.get("responsibleId") || "";
  const responsibleName = searchParams.get("responsibleName") || "";

  const [fetchProvinces, provinceState] =
    useLazyGetMergedBranchProvinceReportQuery();
  const [fetchBranches, branchState] = useLazyGetMergedBranchReportQuery();
  const [fetchResponsible, responsibleState] =
    useLazyGetMergedBranchResponsibleReportQuery();
  const [fetchShipments, shipmentState] =
    useLazyGetMergedBranchShipmentReportQuery();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedKeyword(keyword), 350);
    return () => clearTimeout(timer);
  }, [keyword]);

  const account = viewEntity?.entity?.account?._id;
  const entity = viewEntity?.entity?._id;
  const fetchData = useCallback(() => {
    if (!account || !entity) return;
    const [sortedBy, sortDirection] = sortBy.split(":");
    // Only attach olderThanHours when the toggle is on. Overdue is the
    // DEFAULT, so the default request filters to 48h+; choosing "All Unpaid"
    // sends the parameter-free request identical to the old default shape.
    const common = {
      account,
      entity,
      page: currentPage,
      limit: perPage,
      sortedBy,
      sortDirection,
      keyword: debouncedKeyword.trim() || undefined,
      ...(overdueOnly ? { olderThanHours: 48 } : {}),
    };
    if (level === "provinces") {
      fetchProvinces({
        ...common,
        sortedBy: sortedBy || "name",
      });
    } else if (level === "branches") {
      fetchBranches({
        ...common,
        region: provinceId || undefined,
      });
    } else if (level === "responsible") {
      fetchResponsible({ ...common, branchId, category });
    } else {
      fetchShipments({
        ...common,
        branchId,
        category,
        responsibleId: responsibleId || undefined,
      });
    }
  }, [
    account,
    entity,
    level,
    provinceId,
    branchId,
    category,
    responsibleId,
    currentPage,
    perPage,
    sortBy,
    debouncedKeyword,
    overdueOnly,
    fetchProvinces,
    fetchBranches,
    fetchResponsible,
    fetchShipments,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Excel export — fetches the current drill-down level with `exportAll=true`
  // so the file contains every row that matches the current keyword /
  // overdue filter, regardless of pagination. Mirrors the QRPaymentReport
  // export pattern.
  const [exporting, setExporting] = useState(false);
  const handleExcelExport = async () => {
    if (!account || !entity || exporting) return;
    setExporting(true);
    try {
      const [sortedBy, sortDirection] = sortBy.split(":");
      const baseParams = {
        account,
        entity,
        exportAll: "true",
        sortedBy: sortedBy || "name",
        sortDirection,
        keyword: debouncedKeyword.trim() || undefined,
        ...(overdueOnly ? { olderThanHours: 48 } : {}),
      };
      let response;
      if (level === "provinces") {
        response = await fetchProvinces(baseParams).unwrap();
      } else if (level === "branches") {
        response = await fetchBranches({ ...baseParams, region: provinceId || undefined }).unwrap();
      } else if (level === "responsible") {
        response = await fetchResponsible({ ...baseParams, branchId, category }).unwrap();
      } else {
        response = await fetchShipments({
          ...baseParams,
          branchId,
          category,
          responsibleId: responsibleId || undefined,
        }).unwrap();
      }
      const rows = response?.data || [];

      // Province level: append the unassigned row so the export captures
      // every branch that the on-screen banner shows.
      const exportRows = level === "provinces" && response?.unassigned?.branchCount
        ? [...rows, response.unassigned]
        : rows;

      const sheetData = exportRows.map((row) => buildExportRow(row, level, category));

      const scopeLabel =
        level === "provinces"
          ? "Provinces"
          : level === "branches"
            ? `Branches${provinceName ? `_${provinceName}` : ""}`
            : level === "responsible"
              ? `Responsible${branchName ? `_${branchName}` : ""}_${category === "coc-vendors" ? "COC_Vendors" : category === "users" ? "COD_and_COC_Users" : "COD_Users"}`
              : `Shipments${responsibleName ? `_${responsibleName}` : ""}${branchName ? `_${branchName}` : ""}_${category === "coc-vendors" ? "COC_Vendors" : category === "users" ? "COD_and_COC_Users" : "COD_Users"}`;
      const overdueLabel = overdueOnly ? "_Overdue_48h" : "";
      const fileName = `Merged_Branch_Report_${scopeLabel}${overdueLabel}`;

      exportExcelFile({ sheetData, fileName });
    } catch (error) {
      console.error("Excel export failed:", error);
      setErrorMessage("Failed to export. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const buildExportRow = (row, lvl, cat) => {
    if (lvl === "provinces") {
      return {
        Province: row.name || "Unassigned (no province)",
        Branches: row.branchCount || 0,
        "COD Not Paid": Number(row.codNotPaid || 0).toFixed(2),
        "COC User Not Paid": Number(row.cocUserNotPaid || 0).toFixed(2),
        "Total User Unpaid": userUnpaid(row).toFixed(2),
        "COC Vendor Not Paid": Number(row.cocVendorNotPaid || 0).toFixed(2),
        "Total Not Paid": Number(row.totalNotPaid || 0).toFixed(2),
      };
    }
    if (lvl === "branches") {
      return {
        Branch: row.branchName || "N/A",
        Province: row.regionName || "Unassigned",
        "COD Not Paid": Number(row.codNotPaid || 0).toFixed(2),
        "COC User Not Paid": Number(row.cocUserNotPaid || 0).toFixed(2),
        "Total User Unpaid": userUnpaid(row).toFixed(2),
        "COC Vendor Not Paid": Number(row.cocVendorNotPaid || 0).toFixed(2),
        "Total Not Paid": Number(row.totalNotPaid || 0).toFixed(2),
      };
    }
    if (lvl === "responsible") {
      const base = {
        [cat === "coc-vendors" ? "Vendor" : "User"]: row.name || "N/A",
        "Responsible Type": row.responsibleType || "N/A",
      };
      if (cat === "users") {
        return {
          ...base,
          "COD Not Paid": Number(row.codNotPaid || 0).toFixed(2),
          "COC User Not Paid": Number(row.cocUserNotPaid || 0).toFixed(2),
          "Not Paid": Number(row.totalNotPaid || 0).toFixed(2),
        };
      }
      return {
        ...base,
        "Not Paid": Number(row.totalNotPaid || 0).toFixed(2),
      };
    }
    // shipments
    return {
      "Shipment ID": row.shipmentId || "N/A",
      Report: row.reportType || "N/A",
      "Charge Type": row.chargeType || "N/A",
      "Not Paid": Number(row.charge || 0).toFixed(2),
      "Created At": row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A",
    };
  };

  const state =
    level === "provinces"
      ? provinceState
      : level === "branches"
        ? branchState
        : level === "responsible"
          ? responsibleState
          : shipmentState;
  const data = state.data?.data || [];
  const loading = state.isLoading || state.isFetching;

  const openLevel = useCallback((nextLevel, values = {}) => {
    setKeyword("");
    setSortBy(
      nextLevel === "provinces"
        ? "name:1"
        : nextLevel === "responsible"
          ? "name:1"
          : nextLevel === "shipments"
            ? "createdAt:-1"
            : "branchName:1",
    );
    setSearchParams({ level: nextLevel, ...values }, { replace: true });
  }, [setSearchParams]);

  const columns = useMemo(() => {
    if (level === "provinces") {
      return [
        { name: "Province", selector: (row) => row.name },
        { name: "Branches", selector: (row) => row.branchCount || 0 },
        { name: "COD Not Paid", selector: (row) => row.codNotPaid, cell: (row) => money(row.codNotPaid) },
        { name: "COC User Not Paid", selector: (row) => row.cocUserNotPaid, cell: (row) => money(row.cocUserNotPaid) },
        {
          name: <UserUnpaidHeader />,
          selector: (row) => userUnpaid(row),
          cell: (row) => <UserUnpaidCell row={row} />,
        },
        { name: "COC Vendor Not Paid", selector: (row) => row.cocVendorNotPaid, cell: (row) => money(row.cocVendorNotPaid) },
        { name: "Total Not Paid", selector: (row) => row.totalNotPaid, cell: (row) => money(row.totalNotPaid) },
        {
          name: "Action",
          cell: (row) => <Button endIcon={<ChevronRight />} onClick={() => openLevel("branches", { provinceId: row.provinceId || "none", provinceName: row.name })}>Branches</Button>,
          ignoreRowClick: true,
        },
      ];
    }
    if (level === "branches") {
      return [
        { name: "Branch", selector: (row) => row.branchName },
        { name: "Province", selector: (row) => row.regionName || "Unassigned" },
        { name: "COD Not Paid", selector: (row) => row.codNotPaid, cell: (row) => money(row.codNotPaid) },
        { name: "COC User Not Paid", selector: (row) => row.cocUserNotPaid, cell: (row) => money(row.cocUserNotPaid) },
        {
          name: <UserUnpaidHeader />,
          selector: (row) => userUnpaid(row),
          cell: (row) => <UserUnpaidCell row={row} />,
        },
        { name: "COC Vendor Not Paid", selector: (row) => row.cocVendorNotPaid, cell: (row) => money(row.cocVendorNotPaid) },
        { name: "Total Not Paid", selector: (row) => row.totalNotPaid, cell: (row) => money(row.totalNotPaid) },
        {
          name: "Action",
          cell: (row) => (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="COD and COC User Not Paid" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    openLevel("responsible", {
                      branchId: row._id,
                      branchName: row.branchName,
                      provinceId: provinceId || "none",
                      provinceName,
                      category: "users",
                    })
                  }
                >
                  <Person />
                </IconButton>
              </Tooltip>
              <Tooltip title="COC Vendor Not Paid" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() =>
                    openLevel("responsible", {
                      branchId: row._id,
                      branchName: row.branchName,
                      provinceId: provinceId || "none",
                      provinceName,
                      category: "coc-vendors",
                    })
                  }
                >
                  <Storefront />
                </IconButton>
              </Tooltip>
            </Stack>
          ),
          ignoreRowClick: true,
        },
      ];
    }
    if (level === "responsible") {
      return [
        { name: category === "coc-vendors" ? "Vendor" : "User", selector: (row) => row.name },
        { name: "Responsible Type", selector: (row) => row.responsibleType || "N/A" },
        ...(category === "users" ? [
          { name: "COD Not Paid", selector: (row) => row.codNotPaid, cell: (row) => money(row.codNotPaid) },
          { name: "COC User Not Paid", selector: (row) => row.cocUserNotPaid, cell: (row) => money(row.cocUserNotPaid) },
        ] : []),
        { name: "Not Paid", selector: (row) => row.totalNotPaid, cell: (row) => money(row.totalNotPaid) },
        {
          name: "Action",
          cell: (row) => <Button endIcon={<ChevronRight />} onClick={() => openLevel("shipments", { branchId, branchName, responsibleId: row._id, responsibleName: row.name, category, provinceId, provinceName })}>Shipments</Button>,
          ignoreRowClick: true,
        },
      ];
    }
    return [
      {
        name: "Shipment ID",
        selector: (row) => row.shipmentId,
        cell: (row) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {row.shipmentId || "N/A"}
            <Tooltip title="View shipment detail in a new tab" arrow>
              <IconButton
                size="small"
                onClick={() => window.open(`/app/crm/shipment/${row.shipmentObjectId || row._id}/detail`, "_blank")}
              >
                <OpenInNew fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      { name: "Report", selector: (row) => row.reportType || "N/A" },
      { name: "Charge Type", selector: (row) => row.chargeType },
      { name: "Not Paid", selector: (row) => row.charge, cell: (row) => money(row.charge) },
      { name: "Created At", selector: (row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A" },
    ];
  }, [level, category, branchId, provinceId, provinceName, branchName, openLevel]);

  const basePath = "/app/crm/reports/coc-cod-branch-report";
  const makePath = (nextLevel, values = {}) => {
    const params = new URLSearchParams({ level: nextLevel });
    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, String(value));
      }
    });
    return `${basePath}?${params.toString()}`;
  };
  const provincePath = makePath("provinces");
  const branchesPath = makePath("branches", {
    provinceId: provinceId || "none",
    provinceName,
  });
  const responsiblePath = makePath("responsible", {
    provinceId: provinceId || "none",
    provinceName,
    branchId,
    branchName,
    category,
  });

  const categoryTitle =
    category === "users"
      ? "By COD and COC User"
      : category === "coc-vendors"
        ? "By COC Vendor"
        : "By COC User";

  // When the Overdue 48h+ toggle is on (the default view), the report's root
  // title and screen title change to make the filter obvious in the
  // breadcrumb, page title and browser tab. The "All" view keeps the
  // original strings verbatim.
  const overdueTag = " (Overdue 48h+)";
  const rootTitle = overdueOnly
    ? "Pending COD and COC Not Paid" + overdueTag
    : "Pending COD and COC Not Paid";

  const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    ...(level === "provinces"
      ? [{ title: rootTitle }]
      : [
        { title: rootTitle, path: basePath },
        { title: provinceName || "Province", path: provincePath },
      ]),
    ...(level === "branches" ? [{ title: "Branches" }] : []),
    ...(level === "responsible"
      ? [
        { title: branchName || "Branch", path: branchesPath },
        { title: categoryTitle },
      ]
      : []),
    ...(level === "shipments"
      ? [
        { title: branchName || "Branch", path: branchesPath },
        { title: categoryTitle, path: responsiblePath },
        { title: "Shipments" },
      ]
      : []),
  ];

  const screenTitle =
    (level === "provinces"
      ? "Pending COD and COC Not Paid — Provinces"
      : level === "branches"
        ? "Pending COD and COC Not Paid"
        : level === "responsible"
          ? `${branchName || "Branch"} — ${categoryTitle}`
          : `${responsibleName || "Responsible"} — ${categoryTitle} Shipments`) +
    (overdueOnly ? overdueTag : "");

  const summaryMetrics = [
    { label: "COD Not Paid", value: money(state.data?.codNotPaid) },
    { label: "COC User Not Paid", value: money(state.data?.cocUserNotPaid) },
    {
      label: "Total User Unpaid",
      value: money(
        (Number(state.data?.codNotPaid) || 0) +
          (Number(state.data?.cocUserNotPaid) || 0),
      ),
    },
    { label: "COC Vendor Not Paid", value: money(state.data?.cocVendorNotPaid) },
    { label: "Total Not Paid", value: money(state.data?.totalNotPaid) },
  ];
  const responsibleSummaryMetrics = category === "users"
    ? [
      { label: "COD Not Paid", value: money(state.data?.codNotPaid) },
      { label: "COC User Not Paid", value: money(state.data?.cocUserNotPaid) },
      { label: "Total Not Paid", value: money(state.data?.totalNotPaid) },
    ]
    : [
      { label: "COC Vendor Not Paid", value: money(state.data?.cocVendorNotPaid) },
      { label: "Total Not Paid", value: money(state.data?.totalNotPaid) },
    ];

  useAppMeta(overdueOnly ? "Pending COD and COC Not Paid (Overdue 48h+)" : "Pending COD and COC Not Paid");

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs}>
      <BreadcrumbLayout.Error error={state.error} />
      <BreadcrumbLayout.Paper>
        <Box sx={{ px: 2, pt: 2, display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="h6" fontWeight="bold" sx={{ flexShrink: 0 }}>
            {screenTitle}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {level === "branches" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, pr: 2, borderRight: 1, borderColor: "divider" }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                    {provinceName || "Province"}
                  </Typography>
                  <Chip size="small" color="primary" variant="outlined" label={`${state.data?.totalBranches ?? 0} Branches`} />
                </Box>
              </Box>
            )}
            {level === "responsible" && (
              <Tabs value={category} onChange={(_, value) => openLevel("responsible", { branchId, branchName, provinceId, provinceName, category: value })}>
                <Tab value="users" label="COD + COC Users" />
                <Tab value="coc-vendors" label="COC Vendors" />
              </Tabs>
            )}
            <ToggleButtonGroup
              value={overdueOnly ? "overdue" : "all"}
              exclusive
              size="small"
              onChange={(_, value) => {
                // Guard against deselecting both — MUI emits `null` when the
                // active option is re-clicked, which would leave no selection.
                if (value === null) return;
                setOverdueOnly(value === "overdue");
              }}
              aria-label="Unpaid filter"
            >
              <ToggleButton
                value="overdue"
                aria-label="overdue 48 hours"
                color="error"
                sx={{ gap: 0.5 }}
              >
                <AccessTime fontSize="small" />
                Overdue 48h+
              </ToggleButton>
              <ToggleButton value="all" aria-label="all unpaid">
                All Unpaid
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField size="small" placeholder={level === "shipments" ? "Search shipment..." : "Search..."} value={keyword} onChange={(event) => setKeyword(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            <BaseSelect label="Sort By" value={sortBy} onChange={(event) => setSortBy(event.target.value)} options={level === "provinces" ? [{ label: "Province (A-Z)", value: "name:1" }, { label: "Province (Z-A)", value: "name:-1" }, { label: "Total (High-Low)", value: "totalNotPaid:-1" }] : level === "shipments" ? [{ label: "Newest", value: "createdAt:-1" }, { label: "Amount (High-Low)", value: "charge:-1" }] : [{ label: "Name (A-Z)", value: level === "responsible" ? "name:1" : "branchName:1" }, { label: "Name (Z-A)", value: level === "responsible" ? "name:-1" : "branchName:-1" }, { label: "Total (High-Low)", value: "totalNotPaid:-1" }]} getOptionLabel={(option) => option.label} getOptionValue={(option) => option.value} size="small" />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={handleExcelExport}
              disabled={exporting || loading}
            >
              {exporting ? "Exporting..." : "Export to Excel"}
            </Button>
          </Stack>
        </Box>
        {(level === "provinces" || level === "branches") && (
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2, alignItems: "center", px: 2, pt: 2 }}>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap", flex: 1, justifyContent: "flex-end" }}>
              <Box>
                <Typography variant="body2" fontWeight="normal">
                  Pending COD and COC Summary:
                </Typography>
              </Box>
              {summaryMetrics.map((metric) => (
                <Box key={metric.label}>
                  <Typography variant="body2" color="text.secondary">
                    {metric.label}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
            {level === "provinces" && provinceState.data?.unassigned?.branchCount > 0 && (
              <Box
                sx={{
                  px: 1,
                  py: 0.5,
                  border: 1,
                  borderColor: "primary.main",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Typography variant="caption" color="primary" noWrap>
                  Unassigned Branches ({provinceState.data.unassigned.branchCount}) · {money(provinceState.data.unassigned.totalNotPaid)}
                </Typography>
                <Tooltip title="View Unassigned Branches" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() =>
                      openLevel("branches", {
                        provinceId: "none",
                        provinceName: "Unassigned (no province)",
                      })
                    }
                  >
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
        {(level === "responsible" || level === "shipments") && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}>
            <Stack direction="row" spacing={3} sx={{ flexWrap: "wrap" }}>
              {(level === "responsible" ? responsibleSummaryMetrics : [{ label: "Total Not Paid", value: money(state.data?.totalNotPaid) }]).map((metric) => (
                <Box key={metric.label}>
                  <Typography variant="body2" color="text.secondary">
                    {metric.label}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {metric.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box> : <AppTable columns={columns} data={data} pagination={level !== "provinces"} paginationServer={level !== "provinces"} paginationPerPage={perPage} paginationTotalRows={state.data?.totalItems || 0} paginationDefaultPage={currentPage} onChangePage={handlePageChange} onChangeRowsPerPage={handlePerRowsChange} noDataComponent={<Box sx={{ py: 4 }}>No pending COD or COC found.</Box>} />}
        <Snackbar open={!!errorMessage} autoHideDuration={5000} onClose={() => setErrorMessage("")}><Alert severity="error">{errorMessage}</Alert></Snackbar>
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default MergedBranchReport;
